import {useEffect, useRef, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import {t} from "i18next";
import {Alert, Button, Card, Form, Input} from "antd";
import PermissionGroup from "./PermissionGroup";
import usePermissionGuard from "./usePermissionGuard";
import SimpleLayout from "../../core/components/layout/SimpleLayout";
import ApiService from "../../core/service/ApiService";

/**
 * Hem tam sayfa (route) hem de modal içinde kullanılır.
 * @param roleId   Modal kullanımında düzenlenecek rol; verilmezse route parametresi okunur.
 * @param embedded true ise sayfa iskeleti (SimpleLayout + Card) çizilmez, yalnız form döner.
 * @param onSaved  Kayıt başarılı olduğunda çağrılır (modalı kapatıp listeyi tazelemek için).
 */
export default function RoleCreateEdit({roleId: roleIdProp, embedded = false, onSaved})
{
    const params = useParams();
    const roleId = roleIdProp ?? params.roleId;
    const history = useHistory();

    const formRef = useRef();
    const [role, setRole] = useState({
        permissionIdList: []
    });
    const [loadedPermissionIds, setLoadedPermissionIds] = useState([]);
    const { canGrant } = usePermissionGuard();

    // Kaydedilmiş hali kendinden fazla yetki içeren rol düzenlenemez (sunucu da reddeder).
    const locked = !!roleId && !canGrant(loadedPermissionIds);

    const getRole = async () => {
        if(roleId)
        {
            const resp = await ApiService.Role.GetRole(roleId);
            if(resp.success)
            {
                setRole(resp.data);
                setLoadedPermissionIds(resp.data.permissionIdList ?? []);
            }
        }
    }

    useEffect(() => {
        getRole();
    }, [roleId])

    useEffect(() => {
        formRef.current.setFieldsValue(role)
    }, [role])

    const onFinish = async () => {
        const data = {
            ...formRef.current.getFieldsValue(),
            permissionIdList: role.permissionIdList
        };
        if(roleId)
        {
            const res = await ApiService.Role.EditRole(roleId, data);
            if(res.success)
            {
                onSaved && onSaved()
            }
        }
        else
        {
            const res = await ApiService.Role.CreateRole(data);
            if(res.success)
            {
                history.push(`/app/system/roles/edit/${res.data}`)
            }
        }
    }

    const form = (
        <Form
            ref={formRef}
            layout={'vertical'}
            onFinish={onFinish}
            disabled={locked}
        >
            {locked && (
                <Alert
                    type={'warning'}
                    showIcon={true}
                    style={{marginBottom: 16}}
                    message={t('This role contains permissions you do not have, so you cannot edit it.')}
                />
            )}
            <Form.Item
                label={t('Name')}
                name={'name'}
                required={true}
            >
                <Input />
            </Form.Item>

            <Form.Item>
                <PermissionGroup
                    disabled={locked}
                    selectedPermissions={role.permissionIdList}
                    onChangeSelectedPermissions={(permissions) => {
                        setRole({
                            ...role,
                            permissionIdList: permissions
                        })
                    }}
                />
            </Form.Item>

            <Form.Item>
                <Button htmlType={'submit'} type={'primary'}>{t('Save')}</Button>
            </Form.Item>
        </Form>
    )

    if(embedded)
        return form;

    return (
        <SimpleLayout>
            <Card
                title={!roleId ? t('New Role') : `${t('Edit Role')} - ${role.name}`}
            >
                {form}
            </Card>
        </SimpleLayout>
    )
}
